import React, {
    useContext
} from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import moment from 'moment';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import cx from 'classnames';

import messages from '../../MLSummary.messages'
import styles from '../../MLSummary.module.scss'

import { IntlContext } from '@jutro/legacy/locale';

function MLUmbrellaDrivers({ period, show }) {
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    if (_.isEmpty(period)) {
        return null;
    }

    const filterRatedDriver = (item) => {
        return item.driverType === 'rated';
    };

    const filterHousehold = (item) => {
        return item.driverType === 'household';
    };

    const filterExcludedDriver = (item) => {
        return item.driverType === 'excluded';
    };

    const hasPupExcludedDrivers = () => {
        return period.pupVehOperators_ML && period.pupVehOperators_ML.some((driver) => {
            return driver.driverType === 'excluded';
        });
    };

    const getTypeListDisplayName = (typelist, value) => {
        if (!value) {
            return undefined;
        }
        return viewModelService.productMetadata.get('pc').types.getTypelist(typelist).getCode(value).name;
    };

    const getLicenseStateDisplay = (licenseState) => {
        if (licenseState === 'ca_ml' || licenseState === 'ust_ml' || licenseState === 'fl_ml') {
            return getTypeListDisplayName('Jurisdiction', licenseState);
        }
        return licenseState;
    };

    const translateTypeKey = (typekey) => {
        if (typekey) {
            return translator({id: typekey, defaultMessage: typekey});
        }
    };

    const generateRatedHeader = () => {
        return (
            <thead>
                <tr>
                    <th>{translator(messages.Name)}</th>
                    <th>{translator(messages.DOB)}</th>
                    <th>{translator(messages.Gender)}</th>
                    <th>{translator(messages.MaritalStatus)}</th>
                    <th>{translator(messages.License)}</th>
                </tr>
            </thead>
        );
    }

    const formatDOB = (dateOfBirth) => {
        const date = MLLocalDateUtil.localDate2Date(dateOfBirth);
        return moment(date).format('MM/DD/YYYY');
    }

    const generateRatedDriversTable = () => {
        return (
            <>
                <h2 className="section-header vertical-spacing-20">
                    {translator(messages.Drivers)}
                </h2>
                <table className={cx('table table-striped vertical-spacing-40', styles.tableStriped)}>
                    {generateRatedHeader()}
                    <tbody>
                        {period.pupVehOperators_ML.filter((driver) => filterRatedDriver(driver)).map((driver) => {
                            return (
                                <>
                                    <tr>
                                        <td><strong>{driver.contact.displayName}</strong></td>
                                        <td>{formatDOB(driver.contact.dateOfBirth)}</td>
                                        <td>{translateTypeKey(getTypeListDisplayName('GenderType', driver.contact.gender))}</td>
                                        <td>{translateTypeKey(getTypeListDisplayName('MaritalStatus', driver.contact.maritalStatus))}</td>
                                        <td>{driver.licenseNumber} {(driver.licenseState) ? <span>({getLicenseStateDisplay(driver.licenseState)})</span> : <></>}</td>
                                    </tr>
                                </>
                            );
                        })}
                    </tbody>
                </table>
            </>
        );
    }

    const generateExcludedHeader = () => {
        return (
            <thead>
                <tr>
                    <th>{translator(messages.Name)}</th>
                    <th>{translator(messages.DOB)}</th>
                    <th>{translator(messages.Gender)}</th>
                    <th>{translator(messages.MaritalStatus)}</th>
                    <th>{translator(messages.RelationshipToInsured)}</th>
                    <th>{translator(messages.ReasonNotListed)}</th>
                </tr>
            </thead>
        );
    }

    const generateExcludedDriversTable = () => {
        if(hasPupExcludedDrivers() && period.canExcludeDrivers_ML) {
            return (
                <div>
                    <h2 className="section-header vertical-spacing-20">
                        {translator(messages.ExcludedDrivers)}
                    </h2>
                    <table className={cx('table table-striped vertical-spacing-40', styles.tableStriped)}>
                        {generateExcludedHeader()}
                        <tbody>
                            {period.pupVehOperators_ML.filter((driver) => filterExcludedDriver(driver)).map((driver) => {
                                return (
                                    <>
                                        <tr>
                                            <td>{driver.contact.displayName}</td>
                                            <td>{formatDOB(driver.contact.dateOfBirth)}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('GenderType', driver.contact.gender))}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('MaritalStatus', driver.contact.maritalStatus))}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('InsuredRelationType_ML', driver.relationshipToInsured))}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('DriverStatusType_ML', driver.driverStatusForAdding))}</td>
                                        </tr>
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );    
        }
        return (<></>);
    }

    const isHouseholdDriver = period.pupVehOperators_ML.find((driver) => {
        return driver.driverType === 'household';
    });

    const generateHouseholdHeader = () => {
        return (
            <thead>
                <tr>
                    <th>{translator(messages.Name)}</th>
                    <th>{translator(messages.DOB)}</th>
                    <th>{translator(messages.Gender)}</th>
                    <th>{translator(messages.RelationshipToInsured)}</th>
                    <th>{translator(messages.ReasonNotListed)}</th>
                </tr>
            </thead>
        );
    }

    const generateHouseholdMembersTable = () => {
        if(isHouseholdDriver) {
            return (
                <>
                    <h2 className="section-header vertical-spacing-20">
                        {translator(messages.HouseholdMembers)}
                    </h2>
                    <table className={cx('table table-striped', styles.tableStriped)}>
                        {generateHouseholdHeader()}
                        <tbody>
                            {period.pupVehOperators_ML.filter((householdMember) => filterHousehold(householdMember)).map((householdMember) => {
                                return (
                                    <>
                                        <tr>
                                            <td><strong>{householdMember.contact.displayName}</strong></td>
                                            <td>{formatDOB(householdMember.contact.dateOfBirth)}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('GenderType', householdMember.contact.gender))}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('InsuredRelationType_ML', householdMember.relationshipToInsured))}</td>
                                            <td>{translateTypeKey(getTypeListDisplayName('DriverStatusType_ML', householdMember.driverStatusForAdding))}</td>
                                        </tr>
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            );
        }
    }

    if (show) {
        return (
            <div className="vertical-spacing-40">
                {generateRatedDriversTable()}
                {generateExcludedDriversTable()}
                {generateHouseholdMembersTable()}
            </div>
        );
    }
    return ( <></> );
}

MLUmbrellaDrivers.propTypes = {
    period: PropTypes.shape({}),
    show: PropTypes.bool
};
MLUmbrellaDrivers.defaultProps = {
    period: {},
    show: false
};
export default MLUmbrellaDrivers;
